// PNG export of a wallpaper element via html-to-image. Handles font preload,
// autofit, Safari fallbacks.

import { toBlob, toPng } from "html-to-image";
import { applyAutofit, preloadFonts } from "../render/autofit";

export async function exportWallpaperPNG(
  wallpaperEl: HTMLElement,
  filename: string,
): Promise<void> {
  // 1. Make sure web fonts are loaded.
  await preloadFonts();

  // 2. Re-run autofit defensively — autofit is idempotent thanks to the
  //    data-original-text stash, so this is a no-op if already correct.
  applyAutofit(wallpaperEl);

  // 3. Wait one frame for layout to settle. setTimeout(0) instead of rAF
  //    because rAF gets throttled in inactive/headless tabs.
  await new Promise(r => setTimeout(r, 16));

  // 4. Move the element into a fixed-position capture sandbox so html-to-image
  //    sees it at known coordinates with no parent positioning weirdness.
  //    The hidden-render-host has position:fixed; left:-10000px which can
  //    confuse html-to-image's bounding-rect math on some browsers.
  const w = parseInt(wallpaperEl.style.width) || wallpaperEl.clientWidth;
  const h = parseInt(wallpaperEl.style.height) || wallpaperEl.clientHeight;

  const sandbox = document.createElement("div");
  sandbox.style.cssText = `position:fixed; left:0; top:0; width:${w}px; height:${h}px; z-index:-1; opacity:0; pointer-events:none;`;
  const originalParent = wallpaperEl.parentElement;
  const originalNext = wallpaperEl.nextSibling;
  document.body.appendChild(sandbox);
  sandbox.appendChild(wallpaperEl);
  // Reflow before capture
  await new Promise(r => setTimeout(r, 50));

  const opts = {
    width: w,
    height: h,
    canvasWidth: w,
    canvasHeight: h,
    pixelRatio: 1,
    backgroundColor: "#000",
    cacheBust: false,
  };

  const restore = () => {
    if (originalParent) {
      if (originalNext) originalParent.insertBefore(wallpaperEl, originalNext);
      else originalParent.appendChild(wallpaperEl);
    }
    sandbox.remove();
  };

  try {
    const rect = wallpaperEl.getBoundingClientRect();
    console.log(`[png] source rect: ${rect.width}×${rect.height}, expected ${w}×${h}`);
    const blob = await toBlob(wallpaperEl, opts);
    if (!blob) throw new Error("toBlob returned null");
    // Verify dimensions
    const verifyImg = new Image();
    const verifyURL = URL.createObjectURL(blob);
    await new Promise<void>((resolve) => {
      verifyImg.onload = () => resolve();
      verifyImg.onerror = () => resolve();
      verifyImg.src = verifyURL;
    });
    console.log(`[png] PNG output: ${verifyImg.naturalWidth}×${verifyImg.naturalHeight}`);
    URL.revokeObjectURL(verifyURL);
    triggerDownload(blob, filename);
  } catch (err) {
    console.warn("toBlob failed, falling back to toPng:", err);
    const dataUrl = await toPng(wallpaperEl, opts);
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    restore();
  }
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
